import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import {
    Eventcalendar,
    localeDe,
    localeEn,
    type MbscCalendarEvent,
    type MbscEventClickEvent,
    type MbscPageChangeEvent,
} from "@mobiscroll/react";
import type {Availability} from "crqlar-react-common/dist/models/availability/availability.model";
import type {Profile} from "crqlar-react-common/dist/models/profile/profile.model";
import {
    type Reservation,
    ReservationStatus,
} from "crqlar-react-common/dist/models/reservation/reservation.model";
import type {Sector} from "crqlar-react-common/dist/models/sector/sector.model";
import type {MarkedService} from "crqlar-react-common/dist/models/service/service.model";
import {useOrganization} from "crqlar-react-common/dist/state/auth.reducer";
import {addMinutes} from "crqlar-react-common/dist/utils/date.util";
import {getCalendarTimezoneProps, toJsDate, withJsDates} from "crqlar-react-common/dist/utils/calendarTimezone";
import {useAllAgeCategories} from "crqlar-react-common/dist/utils/hooks/ageCategories/useAgeCategories";
import {useListAgendas} from "crqlar-react-common/dist/utils/hooks/agenda/useAgendaData";
import {
    daySlices,
    getTimelineSpanDates,
} from "crqlar-react-common/dist/utils/hooks/reservations/calendar/calendar.util";
import {useAllResourceCategoriesForSector} from "crqlar-react-common/dist/utils/hooks/resources/useAllResourceCategories";
import {useServices} from "crqlar-react-common/dist/utils/hooks/services/useServices";
import {useReadOnly} from "crqlar-react-common/dist/utils/hooks/users/useReadOnly";
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import SingleDatePicker from "../../common/newDatePicker/SingleDatePicker";
import {
    RightSidebarActionButton,
    RightSidebarCreateButton,
    RightSidebarDivider,
    RightSidebarRail,
    RightSidebarRailLayout,
} from "../../layout/rightSidebar/RightSidebarRail";
import CalendarEventServicesTooltip from "../services/CalendarEventServicesTooltip";
import ServiceDialog from "../services/ServiceDialog";
import ServiceEventPreview from "../services/ServiceEventPreview";
import {
    CalendarViewPicker,
    type CalendarViewCapabilities,
    type CalendarViewSettings,
} from "../rail/CalendarViewPicker";
import {WeeklyPlannerAgendaPicker} from "./WeeklyPlannerAgendaPicker";
import {ReservationServicePicker} from "../rail/ReservationServicePicker";
import {useReservationServiceSelection} from "../rail/useReservationServiceSelection";
import {getPermittedSteps} from "../../../utils/reservation.util";
import {
    DEFAULT_CALENDAR_VIEW_SETTINGS,
    useCalendarViewSettings,
} from "../calendar/useCalendarViewSettings";
import {useWeeklyPlannerCalendarData} from "./useWeeklyPlannerCalendarData";
import ReservationDialog from "../reservationDialog/ReservationDialog";

interface Props {
    sector: Sector;
}

const EMPTY_PROFILES: Profile[] = [];

export default function WeeklyPlannerCalendar({sector}: Props) {
    const {t, i18n} = useTranslation();
    const sectorId = sector.id ?? "";
    const organization = useOrganization();
    const readOnly = useReadOnly();
    const agendasQuery = useListAgendas();
    const ageCategories = useAllAgeCategories();
    const resourceCategories = useAllResourceCategoriesForSector(sectorId);
    const agendaStorageKey = `selectedAgenda-${sectorId}`;
    const viewStorageKey = `calendar-view-v2-${sectorId}`;
    const [selectedAgendaId, setSelectedAgendaId] = useState(
        () => localStorage.getItem(agendaStorageKey) ?? "",
    );
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [markedService, setMarkedService] = useState<MarkedService>();
    const [selectedReservation, setSelectedReservation] = useState<Reservation>();
    const [hoveredEvent, setHoveredEvent] = useState<MbscCalendarEvent>();
    const [tooltipAnchor, setTooltipAnchor] = useState<HTMLElement>();
    const tooltipTimer = useRef<ReturnType<typeof setTimeout>>();
    const {settings, setSettings, view} = useCalendarViewSettings(
        viewStorageKey,
        DEFAULT_CALENDAR_VIEW_SETTINGS,
    );

    // Weekly-planner agendas are organization-wide choices in the legacy calendar.
    // Do not narrow them to the route sector: an agenda may intentionally use another sector's services.
    const agendas = useMemo(() => agendasQuery.data ?? [], [agendasQuery.data]);

    useEffect(() => {
        if (agendas.length === 0) return;

        const selectedAgendaExists = agendas.some(agenda => agenda.id === selectedAgendaId);
        const nextAgendaId = selectedAgendaExists ? selectedAgendaId : agendas[0].id;
        if (!nextAgendaId) return;

        if (nextAgendaId !== selectedAgendaId) {
            setSelectedAgendaId(nextAgendaId);
        }
        localStorage.setItem(agendaStorageKey, nextAgendaId);
    }, [agendaStorageKey, agendas, selectedAgendaId]);

    useEffect(() => () => {
        if (tooltipTimer.current) clearTimeout(tooltipTimer.current);
    }, []);

    const selectedAgenda = useMemo(
        () => agendas.find(agenda => agenda.id === selectedAgendaId) ?? agendas[0],
        [agendas, selectedAgendaId],
    );
    const agendaServiceIds = useMemo(
        () => [...(selectedAgenda?.serviceIds ?? [])],
        [selectedAgenda?.serviceIds],
    );
    const agendaServiceIdSet = useMemo(() => new Set(agendaServiceIds), [agendaServiceIds]);
    const loadedAgendaServices = useServices(agendaServiceIds);
    const agendaServices = useMemo(
        () => loadedAgendaServices.filter(service => service.id && agendaServiceIdSet.has(service.id)),
        [agendaServiceIdSet, loadedAgendaServices],
    );
    const serviceSelection = useReservationServiceSelection({
        sectorId,
        viewKey: "calendar",
        availableServices: agendaServices,
    });
    const [rangeStart, rangeEnd] = useMemo(
        () => getTimelineSpanDates(settings.span, selectedDate),
        [selectedDate, settings.span],
    );
    const selectedServiceIds = serviceSelection.services.length === 0 || serviceSelection.allSelected
        ? undefined
        : serviceSelection.selectedIds;
    const calendarData = useWeeklyPlannerCalendarData({
        agenda: selectedAgenda,
        selectedServiceIds,
        span: settings.span,
        startDate: rangeStart,
        endDate: rangeEnd,
    });

    const dayParts = useMemo(() => {
        const [morningStart, morningEnd] = daySlices("morning", "day");
        const [dayStart, dayEnd] = daySlices("day", "day");
        const [eveningStart, eveningEnd] = daySlices("evening", "day");

        return [
            {value: "all_day" as const, label: t("All day")},
            {value: "morning" as const, label: t("Morning"), startTime: morningStart, endTime: morningEnd},
            {value: "day" as const, label: t("Day"), startTime: dayStart, endTime: dayEnd},
            {value: "evening" as const, label: t("Evening"), startTime: eveningStart, endTime: eveningEnd},
            {value: "custom" as const, label: t("Custom")},
        ];
    }, [t]);
    const viewCapabilities = useMemo<CalendarViewCapabilities>(() => ({
        modes: ["timeline", "timeGrid"],
        spans: ["day", "week", "month"],
        steps: getPermittedSteps(settings.span),
        dayParts,
    }), [dayParts, settings.span]);

    const handleViewChange = useCallback((nextSettings: CalendarViewSettings) => {
        const permittedSteps = getPermittedSteps(nextSettings.span);
        setSettings({
            ...nextSettings,
            stepMinutes: permittedSteps.includes(nextSettings.stepMinutes)
                ? nextSettings.stepMinutes
                : permittedSteps[0],
        });
    }, [setSettings]);

    const handleAgendaChange = useCallback((agendaId: string) => {
        setSelectedAgendaId(agendaId);
        localStorage.setItem(agendaStorageKey, agendaId);

        const agenda = agendas.find(candidate => candidate.id === agendaId);
        serviceSelection.setSelectedIds(agenda?.serviceIds ?? []);
    }, [agendaStorageKey, agendas, serviceSelection]);

    const handleCreateReservation = useCallback(() => {
        const now = new Date();
        const start = new Date(selectedDate);
        start.setHours(now.getHours(), now.getMinutes(), 0, 0);
        const defaultService = serviceSelection.services.find(
            service => service.id === sector.defaultServiceId,
        );

        setSelectedReservation({
            expectedStartDate: start,
            expectedEndDate: addMinutes(start, defaultService?.durationMinutes ?? 60),
            serviceId: defaultService?.id,
            sectorId,
            status: ReservationStatus.CONFIRMED,
        });
    }, [sector.defaultServiceId, sectorId, selectedDate, serviceSelection.services]);

    const handleOpenExternal = useCallback(() => {
        if (!selectedAgenda?.id) return;

        const url = `${process.env.REACT_APP_AGENDA_PUBLIC_URL}/${encodeURIComponent(organization?.name ?? "")}/${selectedAgenda.id}?date=${selectedDate.toISOString()}`;
        window.open(url, "_blank", "noopener,noreferrer");
    }, [organization?.name, selectedAgenda?.id, selectedDate]);

    const handlePrint = useCallback(() => {
        if (!selectedAgenda?.id) return;

        window.open(
            `/print/agenda/${selectedAgenda.id}?date=${selectedDate.toISOString()}`,
            "_blank",
            "noopener,noreferrer",
        );
    }, [selectedAgenda?.id, selectedDate]);

    const handlePageChange = useCallback((event: MbscPageChangeEvent) => {
        const candidateDate = toJsDate(event.month ?? event.firstDay);
        const [currentStart, currentEnd] = getTimelineSpanDates(settings.span, selectedDate);
        if (candidateDate < currentStart || candidateDate > currentEnd) {
            setSelectedDate(candidateDate);
        }
    }, [selectedDate, settings.span]);

    const openMarkedService = useCallback((event: MbscCalendarEvent) => {
        const service = calendarData.services.find(candidate => candidate.id === event.serviceId);
        if (!service || !event.start || !event.end || !event.availability) return;

        setHoveredEvent(undefined);
        setMarkedService({
            service,
            availability: event.availability as Availability,
            reservations: (event.reservations ?? []) as Reservation[],
            startDate: toJsDate(event.start),
            endDate: toJsDate(event.end),
            isException: false,
        });
    }, [calendarData.services]);

    const handleEventHoverIn = useCallback((event: MbscEventClickEvent) => {
        if (tooltipTimer.current) clearTimeout(tooltipTimer.current);

        const target = event.domEvent.target as HTMLElement | null;
        const selector = settings.mode === "timeGrid"
            ? ".mbsc-schedule-event"
            : ".mbsc-timeline-event";
        const anchor = target?.closest(selector) as HTMLElement | null;
        if (!anchor) return;

        setTooltipAnchor(anchor);
        setHoveredEvent({...event, event: withJsDates(event.event)} as unknown as MbscCalendarEvent);
    }, [settings.mode]);

    const closeTooltipSoon = useCallback(() => {
        tooltipTimer.current = setTimeout(() => setHoveredEvent(undefined), 200);
    }, []);

    const keepTooltipOpen = useCallback(() => {
        if (tooltipTimer.current) clearTimeout(tooltipTimer.current);
    }, []);

    const renderHeader = useCallback(() => (
        <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            sx={{width: "100%", height: 92, flexShrink: 0, px: 2, boxSizing: "border-box"}}
        >
            <SingleDatePicker
                value={selectedDate}
                onChange={setSelectedDate}
                size="medium"
            />
        </Stack>
    ), [selectedDate]);

    const calendarLocale = i18n.language === "de" ? localeDe : localeEn;
    const visibleResources = settings.mode === "timeGrid" && settings.span === "week"
        ? undefined
        : calendarData.resources;

    return (
        <>
            <RightSidebarRailLayout
                sx={{height: {xs: "calc(100dvh - 56px)", md: "calc(100dvh - 108px)"}}}
            >
                <Box
                    sx={{
                        position: "relative",
                        flex: 1,
                        minWidth: 0,
                        minHeight: 0,
                        "& .mbsc-calendar-controls.mbsc-windows": {p: 0},
                    }}
                >
                    {agendasQuery.isLoading || calendarData.isLoading ? (
                        <Box sx={{height: "100%", display: "flex", alignItems: "center", justifyContent: "center"}}>
                            <CircularProgress/>
                        </Box>
                    ) : agendasQuery.isError || calendarData.isError ? (
                        <Box sx={{height: "100%", display: "flex", alignItems: "center", justifyContent: "center", p: 3}}>
                            <Typography color="error">{t("Could not load calendar")}</Typography>
                        </Box>
                    ) : !selectedAgenda ? (
                        <Box sx={{height: "100%", display: "flex", alignItems: "center", justifyContent: "center", p: 3}}>
                            <Typography color="text.secondary">{t("No weekly planner available")}</Typography>
                        </Box>
                    ) : (
                        <Eventcalendar
                            theme="windows"
                            themeVariant="light"
                            locale={calendarLocale}
                            view={view}
                            {...getCalendarTimezoneProps()}
                            data={calendarData.events}
                            resources={visibleResources}
                            selectedDate={selectedDate}
                            firstDay={1}
                            height="100%"
                            renderHeader={renderHeader}
                            renderScheduleEvent={event => (
                                <ServiceEventPreview
                                    event={event}
                                    service={calendarData.services.find(service => service.id === event.original?.serviceId)}
                                    ageCategories={ageCategories}
                                />
                            )}
                            onPageChange={handlePageChange}
                            onEventClick={event => openMarkedService(event.event)}
                            onEventHoverIn={handleEventHoverIn}
                            onEventHoverOut={closeTooltipSoon}
                            dragToMove={false}
                            dragToResize={false}
                            dragToCreate={false}
                            dragBetweenSlots={false}
                            dragBetweenResources={false}
                            clickToCreate={false}
                        />
                    )}

                    <CalendarEventServicesTooltip
                        isTooltipOpen={!!hoveredEvent}
                        anchor={tooltipAnchor as HTMLElement}
                        handleTooltipClose={() => setHoveredEvent(undefined)}
                        event={hoveredEvent}
                        onMouseEnter={keepTooltipOpen}
                        onMouseLeave={closeTooltipSoon}
                        services={calendarData.services}
                        ageCategories={ageCategories}
                    />
                </Box>

                <RightSidebarRail>
                    <WeeklyPlannerAgendaPicker
                        agendas={agendas}
                        selectedAgendaId={selectedAgenda?.id ?? ""}
                        onSelectedAgendaIdChange={handleAgendaChange}
                    />
                    <CalendarViewPicker
                        value={settings}
                        capabilities={viewCapabilities}
                        onChange={handleViewChange}
                    />
                    <RightSidebarDivider/>
                    <RightSidebarCreateButton
                        label={t("Create reservation")}
                        disabled={readOnly}
                        onClick={handleCreateReservation}
                    />
                    <RightSidebarDivider/>
                    <ReservationServicePicker selection={serviceSelection}/>
                    <RightSidebarActionButton
                        icon={<OpenInNewIcon/>}
                        label={t("View Preview")}
                        disabled={!selectedAgenda?.id}
                        onClick={handleOpenExternal}
                    />
                    <RightSidebarActionButton
                        icon={<PrintOutlinedIcon/>}
                        label={t("Print")}
                        disabled={!selectedAgenda?.id}
                        onClick={handlePrint}
                    />
                </RightSidebarRail>
            </RightSidebarRailLayout>

            <ServiceDialog
                open={!!markedService}
                onClose={() => setMarkedService(undefined)}
                onSave={() => void calendarData.refetch()}
                sector={sector}
                resourceCategories={resourceCategories}
                markedService={markedService}
                profiles={EMPTY_PROFILES}
            />

            <ReservationDialog
                open={!!selectedReservation}
                reservation={selectedReservation}
                onClose={() => setSelectedReservation(undefined)}
                onSave={() => {
                    setSelectedReservation(undefined);
                    void calendarData.refetch();
                }}
                onChangeReservation={setSelectedReservation}
            />
        </>
    );
}
